<script>
import Layout from '@layouts/main'
import { widgetActions } from '@state/helpers'
import { mapGetters, mapState } from 'vuex'

import axios from 'axios'
import zendesk from '@/mixins/zendesk-utils.js'
import commomUtils from '@/mixins/common-utils.js'
import samUtils from '@/mixins/sam-utils.js'
import deepLink from '@/mixins/deeplink.js'
import ENV from '@src/env'
import Raven from 'raven-js'
import GTM_CONSTANTS from '@constants/google-tag-manager'
import AMPLITUDE from '@constants/amplitude'

import { getComponents } from './utils'
import { keysToCamel, getPropsFromObject } from '../../utils/object-utils'

export default {
  page: {
    title: 'Widgets',
    meta: [{ name: 'description', content: 'Widgets' }],
  },
  components: {
    Layout,
  },
  mixins: [zendesk(), commomUtils(), samUtils(), deepLink()],
  data () {
    return {
      loading: false,
      error: null,
      componentList: [],
      params: {},
      userCountry: '',
    }
  },
  computed: {
    ...mapState('widgets', {
      user: state => state.data.SharedData.userData,
      common: state => state.common,
    }),
    ...mapState('zendesk', {
      fetching: state => state.fetching,
    }),
    ...mapGetters('widgets', ['userEnv', 'articleBuilderBody', 'logsUserID']),
    viewEvent () {
      return `VIEW_ARTICLE_${this.common.params.articleId}`
    },
    logsUserId () {
      let { user } = this.common

      user = Object.assign({}, user, {
        userLogsId: `${this.$route.query.country_code.toUpperCase()}_${
          this.logsUserID
          }`,
      })
      return user
    },
    userForLogs () {
      let { user } = this.common

      user = Object.assign({}, user, {
        userLogsId: this.logsUserID,
      })
      return user
    },
    payloadForlogs () {
      const logKeys = [
        'country_code',
        'language',
        'order_id',
        'model_name',
        'origin_transaction',
        'payment_amount',
        'transaction_id',
        'tag',
      ];
      const selectedPayload = keysToCamel(getPropsFromObject(this.$route.query, logKeys));
      const payload = Object.assign({}, this.$route.params, selectedPayload)
      delete payload.token;

      return payload
    },
  },
  watch: {
    $route: 'fetchArticleScreen',
  },
  beforeCreate () {
    const validLAnguages = ['es', 'en', 'pt']
    const { language: queryLanguage } = this.$route.query
    const browserLanguage =
      window.navigator.userLanguage || window.navigator.language
    const language = queryLanguage || browserLanguage
    const [matchLanguage] = validLAnguages.filter(l =>
      language.toLowerCase().includes(l)
    )
    const lang = matchLanguage || browserLanguage

    this.$root._i18n._vm.locale = lang
    axios.defaults.headers.post['Accept-Language'] = lang
  },
  created () {
    try {
      // report entrance for g-analytics
      this.$ga.page(this.$router)
      this.initStore()
      const { screenId } = this.$route.params
      this.params = Object.assign({}, this.$route.query, {
        screen_id: screenId,
      })
      this.validateParams(this.params)
      this.fetchArticleScreen()
    } catch (err) {
      this.error = err.toString()
    }
  },
  methods: {
    ...widgetActions,
    initStore () {
      const { country_code: countryCode, token } = this.$route.query
      this.commitUser({
        countryCode,
        token,
        os: this.getMobileOperatingSystem(),
      })
      this.commitParams(this.$route.params)
      this.commitQuery(this.$route.query)
    },
    fetchArticleScreen () {
      this.error = null
      this.loading = true
      return axios
        .post(
          `${
          ENV.rappi.url[this.userEnv]
          }/api/support-flows/screens/build-article-no-order`,
          this.articleBuilderBody,
          {
            headers: { Authorization: `Bearer ${this.params.token}` },
            validateStatus: function (status) {
              return status >= 200 && status < 400 // default
            },
          }
        )
        .then(
          ({ data, status }) => {
            this.loading = false
            const { screenWidgets } = data.data
            this.componentList = getComponents(screenWidgets)
            this.componentList.map(c => this.commitWidgetData(c))
            this.dispatchEventTrack(this.viewEvent)
          },
          err => {
            Raven.captureException(err)
            const { status } = err.response
            this.loading = false
            this.dispatchEventTrack(
              AMPLITUDE.SERVER_ERROR,
              JSON.parse(JSON.stringify(err))
            )
            if (status >= 500 && status < 600) this.callDeepLink('goto=home')
            else this.error = err.toString()
          }
        )
    },
    getComponent ({ component }) {
      return component()
    },
    onChildChange (childData) { },
    dispatchEventTrack (eventName, payload = this.payloadForlogs) {
      this.$gtm.trackEvent({
        event: GTM_CONSTANTS.LOG_EVENT,
        noninteraction: eventName,
        payload,
        eventType: eventName,
        user: this.userForLogs,
      })
    },
    validQueryLanguage (language) {
      const validLAnguages = ['ES', 'EN', 'PT']
      if (validLAnguages.includes(language.toUpperCase())) return language
      return window.navigator.userLanguage || window.navigator.language
    },
  },
}
</script>

<template>
  <Layout>
    <div :class="$style.container">
      <div :class="[{ [$style.container__spacerBig]: loading || error }, $style.container__spacerSmall]" />
      <div
        v-if="loading || fetching"
        :class="$style.container__loading"
      >
        <v-progress-circular
          :width="3"
          indeterminate
        />
      </div>

      <div
        v-if="error"
        :class="$style.container__error"
      >
        {{ error }}
      </div>
      <div v-if="!fetching">
        <div
          v-for="component in componentList"
          :key="component.key"
          :id="component.label"
        >
          <component
            :class="$style.component__content"
            :is="getComponent(component)"
            :token="params.token"
            :component-data="JSON.parse(JSON.stringify(component.data))"
            :user-country="userEnv"
          />
        </div>
      </div>
    </div>
  </Layout>
</template>

<style scoped lang="scss" module>
@import '@design';
.container {
  &__loading {
    height: 100vh;
    padding-top: 10rem;
    color: $primary-green;
  }

  &__error {
    height: 100vh;
    padding-top: 10rem;
  }

  &__spacer {
    &Small {
      height: 1px;
    }

    &Medium {
      height: 6rem;
    }

    &Big {
      height: 1px;
    }
  }
}
.component {
  width: auto;

  &:last-child {
    width: 0;
    height: 0;
  }

  &__content {
    // padding: 1rem 0;
  }
}
</style>
